import { useLazyQuery, useQuery } from '@apollo/client';
import * as R from 'ramda';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { graphql } from '~/apollo/generated/v3';
import { GeologyType } from '~/apollo/generated/v3/graphql';
import {
  DEPOSITIONAL_WIKI_ENUMS,
  GEOLOGY_TYPE_ENUMS,
} from '~/apollo/operations/wiki';
import { InputGroup } from '~/components/common/InputGroup';
import { Checkbox } from '~/components/ui/forms/Checkbox';
import { ReactSelectInput } from '~/components/ui/forms/react-select-input';
import { Select } from '~/components/ui/forms/select';
import { TextInput } from '~/components/ui/forms/text-input';
import { TinyMceInput } from '~/components/ui/forms/tinymce-input';
import { isEnumMember } from '~/utils/common';
import type { DepositionalWikiFormValues } from '~/utils/modules/depositionalWiki';
import { depositionalWikiTypes } from '~/utils/modules/depositionalWiki';
import { snakeCapsToHuman } from '~/utils/text';

const VOM_EXAMPLE_OPTIONS = graphql(`
  query VomExampleOptions {
    virtualOutcropModelList {
      id
      name
      outcrop {
        id
        approved
      }
    }
  }
`);

export function DepositionalWikiFormFields() {
  const { register, control, watch, setValue } =
    useFormContext<DepositionalWikiFormValues>();

  const { data: geologyTypeEnumsData, loading: loadingGeologyTypeEnums } =
    useQuery(GEOLOGY_TYPE_ENUMS);

  const { data: vomExampleOptionsData, loading: loadingVomExampleOptions } =
    useQuery(VOM_EXAMPLE_OPTIONS);

  const vomList = R.sortBy(
    vom => vom.name,
    vomExampleOptionsData?.virtualOutcropModelList ?? [],
  );

  const [
    loadDepositionalWikiEnums,
    { data: depositionalWikiEnumsData, loading: loadingWikiValue },
  ] = useLazyQuery(DEPOSITIONAL_WIKI_ENUMS);

  const geologyTypeValue = watch('geologyType').toUpperCase();
  const readyForApprovalValue = watch('readyForApproval');
  const publishedValue = watch('published');
  const typeValue = watch('type');

  const geologyTypeRef = useRef(geologyTypeValue);
  const isValidGeologyType = isEnumMember(GeologyType, geologyTypeValue);

  useEffect(() => {
    if (geologyTypeValue && isValidGeologyType) {
      loadDepositionalWikiEnums({
        variables: { geologyType: geologyTypeValue },
      });
    }

    if (geologyTypeValue !== geologyTypeRef.current) {
      setValue('value', []);
    }

    geologyTypeRef.current = geologyTypeValue;
  }, [
    geologyTypeValue,
    isValidGeologyType,
    loadDepositionalWikiEnums,
    setValue,
  ]);

  useEffect(() => {
    if (readyForApprovalValue && publishedValue) {
      setValue('readyForApproval', false);
    }
  }, [readyForApprovalValue, publishedValue, setValue]);

  const geologyTypes = geologyTypeEnumsData?.outcropEnumerations.values ?? [];
  const geologyTypeOptions = geologyTypes.map(gt => ({
    value: gt,
    label: gt,
  }));

  function getValueOptions() {
    if (
      !depositionalWikiEnumsData ||
      !geologyTypeValue ||
      !isValidGeologyType ||
      !typeValue ||
      typeValue === 'NOT_SET' ||
      !(typeValue in depositionalWikiEnumsData)
    ) {
      return [];
    }

    const dwEnums = depositionalWikiEnumsData[typeValue]?.values ?? [];
    const options = dwEnums
      .map(value => ({ value, label: value }))
      .sort(R.ascend(opt => opt.label));
    return R.sortBy(opt => opt.label, options);
  }

  const areFieldsRequired = publishedValue || readyForApprovalValue;

  return (
    <div className="space-y-2">
      <TextInput {...register('title')} label="Title" required />
      <TextInput {...register('author')} label="Author" />

      <div className="grid lg:grid-cols-3 gap-4">
        <div>
          <Controller
            control={control}
            name="geologyType"
            render={({ field }) => (
              <Select
                {...field}
                label={{
                  label: 'Geology type',
                  helpText: 'Select the geology type to get value options',
                }}
                options={geologyTypeOptions}
                disabled={loadingGeologyTypeEnums}
                required={areFieldsRequired}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <Select
                {...field}
                label={{
                  label: 'Wiki type',
                  helpText: 'Select the wiki type to get value options',
                }}
                options={depositionalWikiTypes.map(opt => ({
                  value: opt,
                  label: snakeCapsToHuman(opt),
                }))}
                required={areFieldsRequired}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="value"
            render={({ field }) => (
              <ReactSelectInput
                {...field}
                label={{
                  label: 'Type value',
                  helpText: 'Select the value for the wiki type',
                }}
                options={getValueOptions()}
                required={areFieldsRequired}
                disabled={loadingWikiValue}
                multiple
              />
            )}
          />
        </div>
      </div>

      <TinyMceInput {...register('intro')} label="Introduction" />
      <TinyMceInput {...register('description')} label="Description" />
      <TinyMceInput {...register('diagnostic')} label="Diagnostic" />
      <TinyMceInput {...register('subsurface')} label="Subsurface" />
      <TinyMceInput {...register('reservoir')} label="Reservoir" />
      <TinyMceInput {...register('modeling')} label="Modeling" />
      <TinyMceInput {...register('modernExample')} label="Modern example" />
      <TinyMceInput {...register('references')} label="References" />

      <TextInput
        {...register('mafLink')}
        label={{
          label: 'Modern tool search parameters',
          helpText: 'To generate a link, copy MAF tool URL after the ? sign',
        }}
        className="w-full join-item"
        placeholder="gde=Continental&classification=F&basinType=Foreland&climate=arid"
        renderInput={input => (
          <InputGroup>
            <InputGroup.Addon>/modern?</InputGroup.Addon>
            {input}
          </InputGroup>
        )}
      />

      <Controller
        control={control}
        name="vomExampleId"
        render={({ field }) => (
          <ReactSelectInput
            {...field}
            label="VOM example"
            options={vomList.map(vom => ({
              value: vom.id,
              label: `${vom.name} (${vom.id})`,
              disabled: !vom.outcrop?.approved,
            }))}
            disabled={loadingVomExampleOptions}
          />
        )}
      />

      <div className="card shadow">
        <div className="card-body">
          <h3 className="card-title">Publishing</h3>

          <Checkbox
            {...register('readyForApproval')}
            label="Ready for approval"
            disabled={publishedValue && !readyForApprovalValue}
            color="warning"
          />

          <Checkbox
            {...register('published')}
            label="Published"
            color="success"
          />
        </div>
      </div>
    </div>
  );
}
